/**
 * المرحلة 1.35 — the stay booklet and its account as the screens read them:
 * the day's name as the booklet writes it, the account cards per category,
 * the consumables page, the final statement's lines and who sees which page.
 * Backend/lib/stayAccount.js holds the money rules; this only arranges what
 * the server sent.
 */

import { PERMISSIONS } from './rbac';

const P = PERMISSIONS;

/** The booklet's nursing pages, in its order. */
export const BOOKLET_PAGES = Object.freeze(['care', 'days', 'orders', 'medications', 'consumables', 'visits']);

/** The ways a stay ends. */
export const DISCHARGE_KINDS = Object.freeze(['recovered', 'on_request', 'against_advice', 'death', 'transfer']);

/** The routes a dose is given by, as the medication page offers them. */
export const MED_ROUTES = Object.freeze(['oral', 'iv', 'im', 'sc', 'inhaled', 'topical', 'rectal']);

/** A nurse may take back her own line for this long; accounts at any time (lib/stayAccount.js). */
export const VOID_WINDOW_MINUTES = 30;

/** The ordinal days the booklet spells out; past ten the number is written. */
const ORDINALS = 10;

/** «اليوم الأول» … «اليوم العاشر», then «اليوم 11», through `t`. */
export function dayLabel(day, t) {
  const n = Number(day) || 1;
  return n <= ORDINALS ? t(`stay.day.${n}`) : t('stay.day.n', { n });
}

/** A date's calendar key on the institution's clock. */
export function dayKey(value, timeZone = 'Africa/Cairo') {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(value));
  } catch {
    return new Date(value).toISOString().slice(0, 10);
  }
}

/** The stay's day of a moment, as the server numbers the daily notes: the admission day is day 1. */
export function stayDay(admittedAt, at = new Date(), timeZone = 'Africa/Cairo') {
  const a = Date.parse(`${dayKey(admittedAt, timeZone)}T00:00:00Z`);
  const b = Date.parse(`${dayKey(at, timeZone)}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}

/** The calendar date (YYYY-MM-DD) of the stay's day `n`. */
export function dateOfDay(admittedAt, n, timeZone = 'Africa/Cairo') {
  const start = Date.parse(`${dayKey(admittedAt, timeZone)}T12:00:00Z`);
  if (!Number.isFinite(start)) return null;
  return new Date(start + (Number(n) - 1) * 86400000).toISOString().slice(0, 10);
}

/**
 * The daily-notes pages: one per day from the admission to today (or to the
 * discharge), each with its notes, the days with notes past that kept too.
 */
export function notePages(stay, days = [], { now = new Date(), timeZone = 'Africa/Cairo' } = {}) {
  if (!stay) return [];
  const until = stayDay(stay.admittedAt, stay.dischargedAt || now, timeZone) || 1;
  const last = Math.max(until, ...days.map((d) => d.day));
  const byDay = new Map(days.map((d) => [d.day, d.notes]));
  return Array.from({ length: Math.min(last, 120) }, (_, i) => i + 1).map((day) => ({ day, date: dateOfDay(stay.admittedAt, day, timeZone), notes: byDay.get(day) || [] }));
}

/** May this session take back this line now (the recorder within the window, or accounts)? */
export function mayVoid(charge, { userId, isAccounts = false, now = new Date() } = {}) {
  if (!charge || charge.status === 'void') return false;
  if (isAccounts) return true;
  if (!userId || Number(charge.recordedBy) !== Number(userId)) return false;
  return now.getTime() - new Date(charge.createdAt || charge.occurredAt).getTime() <= VOID_WINDOW_MINUTES * 60000;
}

/** Does this session see the stays (the booklet or the account)? */
export function seesStays(policy, can) {
  if (!policy || policy.enabled !== 'yes') return false;
  return [P.NURSING_CARE, P.BILLING_MANAGE, P.PATIENT_FILE_MANAGE, P.PATIENT_RECORD_VIEW, P.HOSPITAL_FINANCE_VIEW, P.RECEIPT_ISSUE].some((p) => can(p));
}

/** May this session admit a child (the desk, the doctor, accounts)? */
export const admits = (can) => [P.PATIENT_FILE_MANAGE, P.PRESCRIPTION_WRITE, P.BILLING_MANAGE].some((p) => can(p));

/**
 * The account cards: the live lines grouped by category in the order first
 * charged, each with its total and whether any line waits for review.
 */
export function cardsOf(charges = []) {
  const cards = new Map();
  for (const c of charges) {
    if (c.status === 'void') continue;
    const name = c.categoryName || 'أخرى';
    if (!cards.has(name)) cards.set(name, { categoryName: name, lines: [], total: 0, waiting: 0 });
    const card = cards.get(name);
    card.lines.push(c);
    card.total = Math.round((card.total + Number(c.amount)) * 100) / 100;
    if (c.status === 'recorded') card.waiting += 1;
  }
  return Array.from(cards.values());
}

/** The consumables page: the lines charged at the items a nurse may add. */
export function consumablesOf(charges = [], nursingItems = []) {
  const ids = new Set(nursingItems.map((i) => Number(i.id)));
  return charges.filter((c) => c.status !== 'void' && c.priceItemId && ids.has(Number(c.priceItemId)));
}

/** The voided lines, kept for the record. */
export const voidedOf = (charges = []) => charges.filter((c) => c.status === 'void');

/**
 * The final statement's rows: one per category as the server totalled it,
 * then the total, the paid and the remaining.
 */
export function statementOf(totals) {
  if (!totals) return null;
  return {
    rows: totals.byCategory.filter((r) => r.total > 0).map((r) => ({ label: r.categoryName, amount: r.total })),
    total: totals.total,
    paid: totals.paid,
    remaining: totals.remaining,
    pending: totals.pending,
    final: totals.final,
  };
}

/**
 * The orders page: the latest word on each of the four headings, and every
 * treatment line, the newest date first (a line with no date takes its order's).
 */
export function latestOrders(orders = []) {
  const sorted = [...orders].sort((a, b) => String(a.at).localeCompare(String(b.at)));
  const head = { vitals: null, feeding: null, fluids: null, oxygen: null };
  for (const o of sorted) for (const k of Object.keys(head)) if (o.payload && o.payload[k]) head[k] = o.payload[k];
  const keyOf = (l) => `${l.date || String(l.at).slice(0, 10)}|${l.at}`;
  const lines = sorted.flatMap((o) => (o.payload && o.payload.lines ? o.payload.lines.map((l) => ({ ...l, by: o.by, at: o.at })) : []));
  return { head, lines: lines.reverse().sort((a, b) => keyOf(b).localeCompare(keyOf(a))) };
}

/** The care-chart form, empty. */
export const blankCare = () => ({ temperature: '', pulse: '', glucose: '', spo2: '', bp: '', followUp: '' });

/** Is the care row worth sending? One reading at least. */
export const careReady = (form) => ['temperature', 'pulse', 'glucose', 'spo2', 'bp'].some((k) => String(form[k] ?? '').trim() !== '');

/** The doctor's orders form, empty, with one treatment line dated today. */
export function blankOrder(today) {
  return { vitals: '', feeding: '', fluids: '', oxygen: '', lines: [{ date: today, treatment: '' }] };
}

/** What `POST /api/stays/:id/records` is sent for the doctor's orders: the filled parts only. */
export function orderPayload(form) {
  const payload = {};
  for (const key of ['vitals', 'feeding', 'fluids', 'oxygen']) {
    const v = String(form[key] || '').trim();
    if (v) payload[key] = v;
  }
  payload.lines = (form.lines || []).map((l) => ({ date: l.date || null, treatment: String(l.treatment || '').trim() })).filter((l) => l.treatment);
  return { kind: 'order', payload };
}

/** Is the order worth sending? One heading or one treatment line. */
export const orderReady = (form) => ['vitals', 'feeding', 'fluids', 'oxygen'].some((k) => String(form[k] || '').trim()) || (form.lines || []).some((l) => String(l.treatment || '').trim());

/** The admission form, empty, admitted now. */
export function blankAdmission(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  const local = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return { unitItemId: '', attendingDoctorId: '', responsibleUserId: '', diagnosis: '', admittedAt: local };
}

/** What `POST /api/stays` is sent from the admission form. */
export function admissionPayload(patientId, form) {
  return {
    hospitalPatientId: patientId,
    unitItemId: form.unitItemId ? Number(form.unitItemId) : null,
    attendingDoctorId: form.attendingDoctorId ? Number(form.attendingDoctorId) : null,
    responsibleUserId: form.responsibleUserId ? Number(form.responsibleUserId) : undefined,
    diagnosis: String(form.diagnosis || '').trim() || null,
    admittedAt: form.admittedAt ? new Date(form.admittedAt).toISOString() : undefined,
  };
}

/** The stay units a centre admits to: the price list's per-day items (all of them when none is per day). */
export function unitChoices(units = []) {
  const perDay = units.filter((u) => u.unit === 'day');
  return perDay.length ? perDay : units;
}

/** The printed consent that goes with each way out (domain/centerForms.js prints it). */
export const DISCHARGE_FORM = Object.freeze({ recovered: 'recovered', on_request: 'on_request', against_advice: 'against_advice', death: 'death', transfer: null });

/** A care-chart row's body from the form: the filled numbers only. */
export function careBody(form) {
  const payload = {};
  for (const key of ['temperature', 'pulse', 'glucose', 'spo2', 'bp']) {
    const raw = String(form[key] ?? '')
      .trim()
      .replace(/[٠-٩]/g, (c) => '٠١٢٣٤٥٦٧٨٩'.indexOf(c))
      .replace('٫', '.');
    if (raw) payload[key] = key === 'bp' ? raw : Number(raw);
  }
  return { kind: 'vitals', payload, text: String(form.followUp || '').trim() || undefined };
}

/** The payment form's default: what remains, never less than nothing. */
export const defaultPayment = (totals) => (totals && totals.remaining > 0 ? String(totals.remaining) : '');

/**
 * What is left on the account, as it is read out: owed by the family, or —
 * when the deposit is ahead of the days charged so far — their credit, never
 * a minus sign.
 */
export function leftOf(remaining) {
  const n = Number(remaining) || 0;
  return { due: n > 0, credit: n < 0, amount: Math.abs(n) };
}
